import React from 'react'
import { DashboardCount } from './Components/DashboardCount/DashboardCount'
import { Align, OptionsProps, Table } from 'components/Table/Table'
import { getProcurementPurchaseOrders, markAsSentProcurementPurchaseOrder } from 'api'
import { useFilter } from 'hooks/useFilter'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Box, Button, ColorVariant, Flex } from 'styling/GlobalStyles'
import { Pagination } from 'components/Table/Pagination'
import { ProcurementPurchaseOrderResponse } from 'types/procurement/purchaseOrder.model'
import { NormalText, SmallText } from 'styling/TypographyV2'
import { Badge } from 'components/Badge/Badge'
import { PROCUREMENT_PURCHASE_ORDER_STATUS_META } from 'constants/meta'
import { convertToDate } from 'utils/helpers'
import { ProgressBar } from 'components/ProgressBar/ProgressBar'
import { ProcurementPurchaseOrderStatus } from 'types/procurement/enums'
import { OrderDashboardCount } from './Components/DashboardCount/OrderDashboardCount'
import { toast } from 'react-toastify'
import { Spinner } from 'components/Loading/Loading'

export const PurchaseOrders = () => {
  const { values } = useFilter()
  const { data, isLoading } = useQuery({
    queryKey: ['procurement-purchase-orders', values],
    queryFn: () =>
      getProcurementPurchaseOrders({
        page: values.page,
        limit: values.limit,
      }),
  })
  const queryClient = useQueryClient()

  const { mutate: markAsSent, isPending } = useMutation({
    mutationFn: (orderId: number | string) => markAsSentProcurementPurchaseOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procurement-purchase-orders'] })
      toast.success('Purchase order marked as sent')
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const options: OptionsProps<ProcurementPurchaseOrderResponse>[] = [
    {
      header: '#P0 NUMBER',
      cell: (item) => (
        <NormalText>
          #PR-{new Date(item.createdAt).getFullYear()}-{item.id}
        </NormalText>
      ),
    },
    {
      header: 'SUPPLIER',
      cell: (item) => (
        <NormalText>
          {item.procurementPurchaseRequest.selectedQuote?.supplier?.name ||
            item.procurementPurchaseRequest.selectedQuote?.supplierName}
        </NormalText>
      ),
    },
    {
      header: 'LINKED PR',
      cell: (item) => <NormalText>{item.id}</NormalText>,
    },
    {
      header: 'STATUS',
      cell: (item) => (
        <Badge hexColor={PROCUREMENT_PURCHASE_ORDER_STATUS_META[item.status].color}>
          {PROCUREMENT_PURCHASE_ORDER_STATUS_META[item.status].label}
        </Badge>
      ),
    },
    {
      header: 'CREATED DATE',
      cell: (item) => (
        <NormalText colorVariant={ColorVariant.SECONDARY}>
          {convertToDate(item.createdAt)}
        </NormalText>
      ),
    },
    {
      header: 'RECEIVING PROGRESS',
      cell: (item) => {
        const percentage =
          (item.items.reduce((acc, item) => acc + (item.receivedQuantity || 0), 0) /
            item.items.reduce(
              (acc, item) => acc + (item.procurementPurchaseRequestItem.quantity || 0),
              0,
            )) *
          100
        return (
          <Flex gap='8px' center>
            <ProgressBar
              percentage={percentage}
              color={PROCUREMENT_PURCHASE_ORDER_STATUS_META[item.status].color}
            />
            <SmallText colorVariant={ColorVariant.SECONDARY}>{Math.round(percentage)}%</SmallText>
          </Flex>
        )
      },
    },
    {
      align: Align.RIGHT,
      header: 'ACTIONS',
      cell: (item) =>
        item.status === ProcurementPurchaseOrderStatus.SendingEmailToSupplier ? (
          <Button
            orange
            onClick={() => {
              markAsSent(item.id)
            }}
          >
            Mark as sent
          </Button>
        ) : (
          <div style={{ height: '2rem' }} />
        ),
    },
  ]

  return (
    <>
      <OrderDashboardCount />
      <Box height='60px' mb='16px' mt='16px'></Box>
      <Table
        options={options}
        items={data?.data || []}
        loading={isLoading}
        emptyListOptions={{
          description: 'No purchase orders match your filters.',
        }}
      />
      <Flex justend width='100%' mt='16'>
        <Pagination itemCount={data?.totalCount || 0} limit={values.limit || 20} />
      </Flex>
      <Spinner loading={isPending} />
    </>
  )
}
